import { useAuthStore } from "@/store/auth";
import { useClientStore } from "@/store/client";
import { useMagasinStore } from "@/store/magasin";
import { useNotificationsStore } from "@/store/notifications";
import { ActionTypes, StatusTypes, usePanierStore } from "@/store/panier";
import apiDriveSmartliste from "@/services/http/api-drive/client/smartliste";
import panierUtils from "@/utils/panier";
import Panierpagecodes from "@/utils/panier-codes";
import { pushProps, usePush } from "@/components/pages/mixins/push-mixin"

export const pagePanierProps = () => ({
  typePanier: {
    type: String,
    required: false,
    default: "drive" // ou multiple ou click_and_collect ou click_and_collect_traiteur_noel
  },
  ...pushProps()
})

export const pagePanierEmits = () => ([
  "goTunnel"
])

export const usePagePanier = (props, context) => {
  const app = useNuxtApp();
  const route = useRoute();

  const { getPushProducts, getPushData, getPushTitle } = usePush(props.pushZones)

  const authStore = useAuthStore();
  const clientStore = useClientStore();
  const magasinStore = useMagasinStore();
  const notificationsStore = useNotificationsStore();
  const panierStore = usePanierStore();

  const smartliste = ref(null)
  const pushListDevantCaisse = ref([])

  const {
    articleQuantite,
    articlesIndisponiblesByTypePanier,
    paniersGroupedByTypePanierBySections,
    isLoading,
    status,
    paniersGroupedByTypePanier,
    isAllSubstitutionAccord,
    hasAbonnement: hasAbonnementInPanier,
    montantSansAbonnement
  } = storeToRefs(panierStore)

  const {
    id: magasinId,
    dispoPremierCreneau,
    isEligibleAbonnementLad,
    montantMinimumLAD,
    delaisSecuritePlanningInHours,
    hasChoixSubstitution
  } = storeToRefs(magasinStore)

  const { isLogged } = storeToRefs(authStore)

  const {
    isPro,
    aboLadMaxAtteint,
    abonnements,
    abonnementExpireInMagasin
  } = storeToRefs(clientStore)

  const { clearByTypePanier, syncArticles } = panierStore
  const { sendToNotificationZone } = notificationsStore

  const showRecettes = computed(() => {
    return useRuntimeConfig().public.MIAM_SHOW_RECETTES && !isPro.value;
  })
  const showSubstitution = computed(() => {
    if (props.typePanier !== "drive") {
      return false;
    }
    return hasChoixSubstitution.value;
  })
  const typePanierLabel = computed(() => {
    return panierUtils.getLabelByTypePanier(props.typePanier);
  })
  const typePanierIcon = computed(() => {
    return panierUtils.getIconByTypePanier(props.typePanier);
  })
  const lignesBySections = computed(() => {
    if (
      paniersGroupedByTypePanierBySections.value &&
      paniersGroupedByTypePanierBySections.value[props.typePanier]
    ) {
      return paniersGroupedByTypePanierBySections.value[props.typePanier];
    }
    return [];
  })
  const lignes = computed(() => {
    if (
      paniersGroupedByTypePanier.value &&
      paniersGroupedByTypePanier.value[props.typePanier]
    ) {
      return paniersGroupedByTypePanier.value[props.typePanier];
    }
    return [];
  })
  const nbProduits = computed(() => {
    return getNbProduits(lignes.value);
  })
  const articlesIndisponibles = computed(() => {
    if (
      articlesIndisponiblesByTypePanier.value &&
      articlesIndisponiblesByTypePanier.value[props.typePanier]
    ) {
      return articlesIndisponiblesByTypePanier.value[props.typePanier];
    }
    return [];
  })
  const abonnementInCurrentMagasin = computed(() => {
    if (abonnements.value === null) {
      return null;
    }
    return (
      abonnements.value.find(
        (abo) => abo.attributes.magasin_id === magasinId.value
      ) || null
    );
  })
  const abonnementExpireInCurrentMagasin = computed(() => {
    return abonnementExpireInMagasin.value(magasinId.value);
  })
  const montantManquantLAD = computed(() => {
    return montantMinimumLAD.value === null
      ? null
      : montantMinimumLAD.value - montantSansAbonnement.value;
  })
  const delaisCCInHours = computed(() => {
    if (props.typePanier !== "click_and_collect") {
      return null;
    }
    return delaisSecuritePlanningInHours.value("CC");
  })

  const setSubstitutionAccordPanier = (value) => {
    const newLignes = lignes.value.map((elem) => {
      return {
        ...elem,
        substitution_accord: value
      }
    });
    syncArticles(newLignes);
  }
  const goTunnel = () => {
    context.emit("goTunnel");
  }
  const clearCart = () => {
    clearByTypePanier(props.typePanier);
    sendToNotificationZone("Le panier à été vidé.");
    window.scrollTo(0, 0);
  }
  const getNbProduits = (lignes) => {
    return lignes.reduce((acc, current) => {
      acc += current.quantite;
      return acc;
    }, 0);
  }
  const getSmartliste = async () => {
    if (isLogged.value && nbProduits.value > 0 && smartliste.value === null) {
      try {
        const { data } = await app.$apiDrive
          .use(apiDriveSmartliste)
          .smartliste(magasinId.value);
        const produits = data.attributes.produits.data;
        smartliste.value = produits
          .filter((item) => {
            return (
              item.attributes.produit.data.attributes.en_stock &&
              !lignes.value.some(
                (article) =>
                  parseInt(item.attributes.produit.data.id) ===
                  parseInt(article.id)
              )
            );
          })
          .slice(0, 10)
          .map((item) => {
            return item.attributes.produit.data;
          });
      } catch (err) { }
    }
  }

  const mounted = () => {
    getSmartliste();
    app.$partenairesEvents.dispatch("page-loaded", route.path, true);
  }
  const created = () => {
    pushListDevantCaisse.value = getPushProducts(Panierpagecodes.PRODUITS_DEVANTCAISSE, 1).filter(article => articleQuantite.value(article.id) === 0).slice(0, 5);
  }

  watch(
    () => status.value,
    (value) => {
      if (
        value.type === StatusTypes.PANIER_SUCCESS &&
        value.action === ActionTypes.CHECK_STATUS
      ) {
        getSmartliste();
      }
    })



  return {
    Panierpagecodes,
    smartliste,
    pushListDevantCaisse,
    articleQuantite,
    articlesIndisponiblesByTypePanier,
    paniersGroupedByTypePanierBySections,
    isLoading,
    status,
    paniersGroupedByTypePanier,
    isAllSubstitutionAccord,
    hasAbonnementInPanier,
    montantSansAbonnement,
    magasinId,
    dispoPremierCreneau,
    isEligibleAbonnementLad,
    montantMinimumLAD,
    delaisSecuritePlanningInHours,
    hasChoixSubstitution,
    isPro,
    aboLadMaxAtteint,
    abonnements,
    abonnementExpireInMagasin,
    showRecettes,
    showSubstitution,
    typePanierLabel,
    typePanierIcon,
    lignesBySections,
    lignes,
    nbProduits,
    articlesIndisponibles,
    abonnementInCurrentMagasin,
    abonnementExpireInCurrentMagasin,
    montantManquantLAD,
    delaisCCInHours,
    setSubstitutionAccordPanier,
    goTunnel,
    clearCart,
    getNbProduits,
    getSmartliste,
    getPushProducts,
    getPushData,
    getPushTitle,
    mounted,
    created
  }
}